import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { cn } from '../../lib/utils'
import type { RejectionCategory } from './types'

interface RejectModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (category: string, note: string) => void
}

const REJECTION_CATEGORIES: RejectionCategory[] = [
  'budget',
  'priority',
  'timing',
  'missing',
  'scope',
  'other',
]

const MIN_NOTE_LENGTH = 10

export function RejectModal({ open, onOpenChange, onConfirm }: RejectModalProps) {
  const { t } = useTranslation('capex')
  const [category, setCategory] = useState<RejectionCategory | null>(null)
  const [note, setNote] = useState('')

  const isValid = category !== null && note.length >= MIN_NOTE_LENGTH

  const handleConfirm = () => {
    if (!isValid || !category) return
    onConfirm(category, note)
    resetAndClose()
  }

  const resetAndClose = () => {
    setCategory(null)
    setNote('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetAndClose() }}>
      <DialogContent data-testid="reject-plan-modal">
        <DialogHeader>
          <DialogTitle>{t('reject_modal.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Category picker */}
          <div className="space-y-2">
            <Label>{t('reject_modal.category_label')}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {REJECTION_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  data-testid={`reject-category-${cat}`}
                  className={cn(
                    'rounded-md border px-3 py-2 text-sm font-medium transition-colors text-left',
                    category === cat
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  {t(`rejection_category.${cat}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Free text */}
          <div className="space-y-1.5">
            <Label htmlFor="reject-note-input">{t('reject_modal.note_label')}</Label>
            <Textarea
              id="reject-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('reject_modal.note_placeholder')}
              rows={4}
              data-testid="reject-note-input"
            />
            <p
              className={cn(
                'text-xs tabular-nums',
                note.length < MIN_NOTE_LENGTH ? 'text-muted-foreground' : 'text-green-600 dark:text-green-400'
              )}
            >
              {note.length} / {MIN_NOTE_LENGTH} {t('reject_modal.chars_min')}
            </p>
          </div>

          {/* Info notice */}
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="text-sm text-muted-foreground">
              {t('reject_modal.info_notice')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            {t('reject_modal.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isValid}
            data-testid="reject-plan-confirm"
          >
            {t('reject_modal.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
